import React, { FunctionComponent } from 'react';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next'

import {
  Box,
  ButtonGroup,
  Grid,
  Paper,
} from '@mui/material';
import Button from '@mui/material/Button';
import {
  Typography
} from '../../../../components/v2/styled';

import HelpersClientWidgets from '../../../../actions/helpers/client_widgets';

// Measures widget settings. Object under one value key (`measures_small`):
//   { mode: 'last_weight' | 'graph' }
// - `mode` : which body the widget renders — 'last_weight' (the client's
//            latest weight: big number + date + goal line, the default) or
//            'graph' (the CoachUserMeasures chart block: weight + goal lines
//            with 30/60-day + month navigation, minus the delete-point dot).
//            Rendered as a 4-button ButtonGroup; 'episode_loss' and 'bmi'
//            are planned modes, disabled for now.
// No cta toggle (the goto buttons are fixed chrome) and no order setting.

export type MeasuresSmallSettingsValue = {
  mode?: 'last_weight' | 'graph';
};

type Props = {
  value: MeasuresSmallSettingsValue;
  onChange: (v: MeasuresSmallSettingsValue) => void;
  // Unused for this widget (no order setting). Kept in the shared
  // SettingsProps shape so the dialog renders it uniformly.
  order?: string[];
  onOrderChange?: (v: string[]) => void;
};

const LABEL_BASE = 'coach.personal_settings.userdetail_widgets.settings.measures_small';

export const CoachSettingsWidgetMeasuresSmall: FunctionComponent<Props> = ({
  value,
  onChange
}) => {
  const {t} = useTranslation(['translations']);

  // Defensive defaults so the dialog works before the first save.
  const mode = HelpersClientWidgets.getMeasuresSmallMode(value);

  return <Grid container spacing={2}>
    <Grid item xs={12}>
      <Typography variant="caption" sx={{color: 'text.secondary'}}>
        {t(`${LABEL_BASE}.description`)}
      </Typography>
    </Grid>

    {/* Widget-level settings. */}
    <Grid item xs={12}>
      <Typography variant="subtitle2" sx={{color: 'text.secondary'}}>
        {t(`${LABEL_BASE}.options.title`)}
      </Typography>
      <Paper variant="outlined" sx={{padding: 1, marginTop: 0.5}}>
        {/* Mode picker: a 4-button ButtonGroup (selected = contained, the
            CourseEdit pattern). Persisted as the `mode` key; unknown values
            fall back to 'last_weight' via the helper getter. Label + group on
            separate lines (flex-wrap with a full-width label). The two
            planned modes (episode_loss, bmi) render disabled. */}
        <Box sx={{display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 1, rowGap: 0.5, padding: 1, borderRadius: 0.5, marginTop: 0.5}}>
          <Typography variant="body2" sx={{color: 'text.secondary', width: '100%'}}>
            {t(`${LABEL_BASE}.options.mode`)}
          </Typography>
          <ButtonGroup orientation="vertical" fullWidth>
            {HelpersClientWidgets.measuresSmallModes.map((m) => {
              const selected = mode === m.key;
              const { Icon } = m;
              return <Button
                key={m.key}
                variant={selected ? "contained" : "outlined"}
                disabled={!!m.disabled}
                onClick={() => onChange({ mode: (m.key as 'last_weight' | 'graph') })}
                startIcon={<Icon />}
                sx={{justifyContent: 'flex-start'}}
              >
                {t(`${LABEL_BASE}.options.mode.${m.key}`)}
              </Button>;
            })}
          </ButtonGroup>
        </Box>
      </Paper>
    </Grid>
  </Grid>;
}

// Read-only status: the active mode icon so the placed box summarises the
// config at a glance.
type StatusProps = {
  value: MeasuresSmallSettingsValue;
  order?: string[];
};

export const CoachSettingsWidgetMeasuresSmallStatus: FunctionComponent<StatusProps> = ({
  value
}) => {
  const theme = useTheme();
  const {t} = useTranslation(['translations']);

  const mode = HelpersClientWidgets.getMeasuresSmallMode(value);

  return <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.5, marginTop: 1}}>
    <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1.5}}>
      <Box title={t(`${LABEL_BASE}.options.mode.${mode}`)}>
        {(() => {
          const modeItem = HelpersClientWidgets.measuresSmallModes.find((m) => m.key === mode) || HelpersClientWidgets.measuresSmallModes[0];
          const { Icon } = modeItem;
          return <Icon sx={{ fontSize: '1.1rem', color: theme.palette.primary.main }} />;
        })()}
      </Box>
    </Box>
  </Box>;
}
